import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiMoreVertical, FiTrash2 } from "react-icons/fi";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import "../../App.css";
import DocumentStats from "../editor/DocumentStats";
import DailyJournalCard from "./DailyJournalCard";
import DeleteConfirmModal from "../modals/DeleteConfirmModal";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

function formatDateOrTime(dateString) {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatBitsPreview(bits, maxLength = 70) {
  if (!Array.isArray(bits)) return "";
  const text = bits.filter(Boolean).join(", ");
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + "…";
}

function DocumentList({ token }) {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({
    documentCount: 0,
    totalJokes: 0,
    totalBits: 0,
    totalTimeDisplay: "0:00",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const requestSeqRef = useRef(0);
  const [menu, setMenu] = useState(null);                // { anchorEl, doc } for the open card menu
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState(null);
  const [statsVersion, setStatsVersion] = useState(0);   // bump to refetch stats after a delete

  const handleDelete = async (doc) => {
    setConfirmDeleteDoc(null);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/documents/${doc.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      setStatsVersion((v) => v + 1);
    } catch (e) {
      setError(`Failed to delete "${doc.name || doc.id}": ${e.message}`);
    }
  };

  useEffect(() => {
    async function fetchStats() {
      try {
        const statsRes = await fetch(`${API_BASE_URL}/documents/stats`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats({
            documentCount: statsData.documentCount || 0,
            totalJokes: statsData.totalJokes || 0,
            totalBits: statsData.totalBits || 0,
            totalTimeDisplay: statsData.totalTimeDisplay || "0:00",
          });
        } else {
          setStats({ documentCount: 0, totalJokes: 0, totalBits: 0, totalTimeDisplay: "0:00" });
        }
      } catch (e) {
        setStats({ documentCount: 0, totalJokes: 0, totalBits: 0, totalTimeDisplay: "0:00" });
      }
    }

    if (token) {
      fetchStats();
    }
  }, [token, statsVersion]);

  useEffect(() => {
    async function fetchDocuments() {
      const requestId = ++requestSeqRef.current;
      const isFirstLoad = requestId === 1;
      if (isFirstLoad) {
        setInitialLoading(true);
      } else {
        setIsSearching(true);
      }
      setError(null);
      try {
        const docsUrl = new URL(`${API_BASE_URL}/documents`);
        const trimmedSearch = searchTerm.trim();
        if (trimmedSearch) {
          docsUrl.searchParams.set("search", trimmedSearch);
        }

        const res = await fetch(docsUrl.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        if (res.ok) {
          const data = await res.json();
          if (requestId === requestSeqRef.current) {
            setDocuments(data);
          }
        } else {
          let errorMsg = "Failed to load documents.";
          try {
            const errJson = await res.json();
            if (errJson && errJson.error) {
              errorMsg += ` (${errJson.error})`;
            }
          } catch {
            // ignore JSON parse errors
          }
          if (requestId === requestSeqRef.current) {
            setDocuments([]);
            setError(errorMsg);
          }
        }
      } catch (e) {
        if (requestId === requestSeqRef.current) {
          setDocuments([]);
          setError("Failed to load documents." + e.message);
        }
      }
      if (requestId === requestSeqRef.current) {
        setInitialLoading(false);
        setIsSearching(false);
      }
    }

    if (token) {
      fetchDocuments();
    }
  }, [token, searchTerm]);

  if (initialLoading) return <div>Loading documents...</div>;

  const sortedDocuments = [...documents].sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
  );

  return (
    <div className="w-full max-w-none px-0 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      <div className="col-span-full mt-2 mb-2 px-2">
        <DocumentStats stats={stats} />
      </div>
      <div className="col-span-full mb-2 px-2 flex justify-center">
        <div className="relative w-full max-w-md">
          <svg
            className="absolute left-2 top-1/2 text-gray-400"
            style={{ transform: "translateY(-50%)" }}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Filter documents..."
            className="w-full rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 bg-white pl-8 pr-3 py-1.5 text-sm text-gray-700 focus:outline-none transition"
          />
        </div>
      </div>
      {error && (
        <div className="col-span-full -mt-1 mb-2 px-2 text-xs text-center" style={{ color: "red" }}>
          {error}
        </div>
      )}
      {/* New Document Card */}
      <div style={{ margin: "4px" }}>
        <Link
          to="/document_editor"
          className="rounded-lg overflow-hidden border border-gray-300 hover:border-blue-500 transition flex flex-col w-full bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
          style={{ height: "160px", textDecoration: "none" }}
        >
          <div className="px-4 py-2">
            <div className="text-gray-800 text-sm font-medium">New Document</div>
          </div>
          <div className="flex-1 flex items-center justify-center bg-gray-50 border-t border-gray-200">
            <div className="text-3xl text-blue-600">+</div>
          </div>
        </Link>
      </div>
      <DailyJournalCard token={token} />
      {/* Existing Documents */}
      {sortedDocuments.length === 0 ? (
        <div className="col-span-full flex items-center justify-center text-gray-500" style={{ minHeight: "120px" }}>
          No documents found.
        </div>
      ) : (
        sortedDocuments.map((doc) => {
          return (
            <div key={doc.id} style={{ margin: "4px", position: "relative" }}>
              <Link
                to={`/document_editor/${doc.id}`}
                className="rounded-lg overflow-hidden border border-gray-300 hover:border-blue-500 transition flex flex-col w-full bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                style={{ height: "160px", textDecoration: "none" }}
              >
                <div className="px-4 py-2" style={{ paddingRight: 28 }}>
                  <div
                    className="text-gray-800 w-full"
                    style={{
                      fontFamily: "'Courier Prime', 'Courier New', monospace",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textAlign: "left",
                    }}
                  >
                    {doc.name || doc.id}
                  </div>
                  <div className="flex items-center gap-1 text-gray-500 mt-1" style={{ fontSize: "0.65rem" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 2h9l5 5v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" fill="#1d4ed8" />
                      <path d="M15 2v5h5" fill="#1e40af" />
                    </svg>
                    <span>{formatDateOrTime(doc.updated_at)}</span>
                  </div>
                </div>

                {/* Content preview area - lists extracted bits, if any */}
                <div
                  className="flex-1 bg-gray-50 border-t border-gray-200 overflow-hidden flex items-center justify-center text-center text-gray-600 px-2"
                  style={{ fontFamily: "'Courier Prime', 'Courier New', monospace", fontSize: "0.8rem" }}
                >
                  {formatBitsPreview(doc.bits)}
                </div>
              </Link>
              {/* Options menu button, outside the Link so clicking it doesn't open the document */}
              <button
                type="button"
                aria-label={`Options for ${doc.name || doc.id}`}
                title="Options"
                onClick={(e) => setMenu({ anchorEl: e.currentTarget, doc })}
                className="absolute rounded p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                style={{ top: 6, right: 4 }}
              >
                <FiMoreVertical size={16} />
              </button>
            </div>
          );
        })
      )}
      <Menu anchorEl={menu?.anchorEl} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            setConfirmDeleteDoc(menu.doc);
            setMenu(null);
          }}
          sx={{ color: "red" }}
        >
          <ListItemIcon sx={{ color: "inherit" }}>
            <FiTrash2 />
          </ListItemIcon>
          <Typography>Delete</Typography>
        </MenuItem>
      </Menu>
      {confirmDeleteDoc && (
        <DeleteConfirmModal
          name={confirmDeleteDoc.name || confirmDeleteDoc.id}
          onConfirm={() => handleDelete(confirmDeleteDoc)}
          onCancel={() => setConfirmDeleteDoc(null)}
        />
      )}
    </div>
  );
}

export default DocumentList;
